import React, { useCallback, useMemo, useRef } from 'react';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { AccessStatus } from 'reactApp/components/SharingNew/SharingNew.types';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { LinkIcon } from 'reactApp/ui/VKUIIcons';
import { copy } from 'reactApp/utils/copyToClipboard';
import { sendGa } from 'reactApp/utils/ga';

import styles from '../EmptyFolder.css';
import { EItemType, gaSuffix } from '../EmptyFolder.types';

export const useCoshare = (id: string, publicUrl?: string, isAlbum?: boolean) => {
    const coshareRef = useRef<HTMLInputElement>(null);
    const Coshare = useMemo(() => (
        <div className={styles.icon} ref={coshareRef}>
            <LinkIcon />
        </div>
    ), [coshareRef]);

    const handlePublishing = useCallback(() => {
        sendGa(gaSuffix, 'publish');

        publishHelper({
            item: { id },
            // При нажатии на "Включить совместную загрузку", надо выставлять права на загрузку
            defaultAccessRights: publicUrl || isAlbum ? undefined : AccessStatus.Upload,
            publishFrom: 'empty-folder',
        });
    }, [id, isAlbum, publicUrl]);

    const onCopyClick = useCallback(
        (event) => {
            event.stopPropagation();
            event.nativeEvent?.stopImmediatePropagation?.();

            if (!publicUrl) {
                return;
            }

            copy(publicUrl)
                .then(({ hasBeenCopied }): void => {
                    if (hasBeenCopied) {
                        snackbarController.showSnackbar({
                            type: SnackbarTypes.success,
                            text: 'Ссылка успешно скопирована',
                            id: 'copyLink',
                            closable: true,
                        });
                    }
                })
                .catch(() =>
                    snackbarController.showSnackbar({
                        type: SnackbarTypes.failure,
                        text: 'Не удалось скопировать ссылку',
                        id: 'copyLink',
                        closable: true,
                    })
                );
        },
        [publicUrl]
    );

    const additionLinks = useMemo(() => {
        if (!publicUrl) {
            return;
        }

        return (
            <div className={styles.buttonGroup}>
                <div className={styles.button} onClick={onCopyClick}>
                    Скопировать ссылку
                </div>
                <div className={styles.button}>
                    <span className={styles.textOverflow}>Открыть{String.fromCharCode(160)}настройки доступа</span>
                </div>
            </div>
        )
    }, [onCopyClick, publicUrl])

    return useCallback(
        (accessRights?: AccessStatus) => {
            if (accessRights === AccessStatus.Upload && publicUrl) {
                return {
                    title: 'Совместная загрузка включена',
                    annotation: `Теперь другие пользователи могут смотреть и загружать ${isAlbum? 'фото и видео в этот альбом' : 'файлы в эту папку'}`,
                    onClick: handlePublishing,
                    type: EItemType.COSHARING,
                    Icon: Coshare,
                    additionLinks,
                };
            }

            if (accessRights === AccessStatus.ReadOnly && publicUrl) {
                return {
                    title: 'Открыт доступ по ссылке',
                    annotation: `Теперь другие пользователи могут просматривать ${isAlbum? 'фото и видео в этом альбоме' : 'файлы в этой папке'}`,
                    onClick: handlePublishing,
                    type: EItemType.COSHARING,
                    Icon: Coshare,
                    additionLinks,
                };
            }

            return {
                title: isAlbum ? 'Настроить доступ' : 'Включить совместную загрузку',
                annotation: isAlbum? 'Другие пользователи смогут смотреть фото и видео в этом альбоме' : 'Другие пользователи смогут смотреть и загружать файлы в эту папкy',
                onClick: handlePublishing,
                type: EItemType.COSHARING,
                Icon: Coshare,
            };
        },
        [Coshare, additionLinks, handlePublishing, isAlbum, publicUrl]
    );
};
